import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from '../../config/firebase';
import { loginApi, registerApi, verifyOTPApi, resendOTPApi, googleLoginApi } from '../../api/auth.api';
import { forgotPasswordApi, verifyResetOTPApi, resetPasswordApi } from '../../api/password.api';
import useAuthStore from '../../store/authStore';
import { useAuthModal } from '../../context/AuthModalContext';
import { registerLoginModalTrigger } from '../../store/cartStore';
import Input from '../ui/Input';
import Button from '../ui/Button';
import OTPInput from '../ui/OTPInput';
import { rules } from '../../utils/validators';
import cn from '../../utils/cn';

const EyeIcon = ({ show }) => show ? (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
  </svg>
) : (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const strengthChecks = (p) => [
  { label: 'At least 8 characters', pass: p.length >= 8 },
  { label: 'One uppercase letter',  pass: /[A-Z]/.test(p) },
  { label: 'One number',            pass: /[0-9]/.test(p) },
];

// ── Social buttons ────────────────────────────────────────────────────────
const SocialButtons = ({ onSuccess }) => {
  const [loading, setLoading] = useState(false);

  const handleGoogle = async () => {
    setLoading(true);
    try {
      googleProvider.setCustomParameters({ prompt: 'select_account' });
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken();
      const { data } = await googleLoginApi({ idToken });
      onSuccess(data.data.user, data.data.token, data.message);
    } catch (err) {
      if (err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request') return;
      const msg = err?.response?.data?.message || err?.message || 'Google login failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex gap-2 mt-3">
      <button type="button" onClick={handleGoogle} disabled={loading}
        className="flex-1 flex items-center justify-center gap-2 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
        {loading ? (
          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
          </svg>
        ) : (
          <svg className="w-4 h-4" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
        )}
        Google
      </button>
      <button type="button" onClick={() => toast.info('Facebook login coming soon!')}
        className="flex-1 flex items-center justify-center gap-2 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
        <svg className="w-4 h-4" fill="#1877F2" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
        Facebook
      </button>
    </div>
  );
};

// ── Tabs ──────────────────────────────────────────────────────────────────
const Tabs = ({ active, onChange }) => (
  <div className="flex rounded-xl border border-gray-200 overflow-hidden mb-5">
    <button type="button" onClick={() => onChange('email')}
      className={`flex-1 py-2 text-sm font-medium transition-colors ${active === 'email' ? 'bg-primary-600 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>
      Email
    </button>
    <button type="button" onClick={() => onChange('phone')}
      className={`flex-1 py-2 text-sm font-medium transition-colors ${active === 'phone' ? 'bg-primary-600 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>
      Phone
    </button>
  </div>
);

// ── Login ─────────────────────────────────────────────────────────────────
const StepLogin = () => {
  const { setAuth } = useAuthStore();
  const { closeLoginModal, goTo } = useAuthModal();
  const { register, handleSubmit, formState: { errors }, reset } = useForm();
  const [showPw, setShowPw] = useState(false);
  const [tab, setTab] = useState('email');

  const { mutate, isLoading } = useMutation({
    mutationFn: loginApi,
    onSuccess: ({ data }) => {
      if (data.statusCode === 403) {
        toast.info('Please verify your email first.');
        goTo('verify-otp', { email: data.data.email, purpose: 'email-verify' });
        return;
      }
      setAuth(data.data.user, data.data.token);
      toast.success(data.message);
      reset();
      closeLoginModal();
    },
  });

  const onSubmit = (d) => {
    if (tab === 'phone') { toast.info('Phone login coming soon!'); return; }
    mutate({ email: d.email, password: d.password });
  };

  const handleSocialSuccess = (user, token, message) => {
    setAuth(user, token);
    toast.success(message || 'Logged in with Google!');
    reset();
    closeLoginModal();
  };

  return (
    <>
      <div className="mb-5">
        <h2 className="text-2xl font-bold text-gray-900">Sign in</h2>
        <p className="text-gray-500 mt-1 text-sm">Welcome back</p>
      </div>

      <Tabs active={tab} onChange={setTab} />

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        {tab === 'email' ? (
          <Input id="l-email" type="email" label="Email address" placeholder="you@example.com"
            error={errors.email?.message} {...register('email', rules.email)} />
        ) : (
          <Input id="l-phone" type="tel" label="Phone Number" placeholder="03XX-XXXXXXX"
            error={errors.phone?.message}
            {...register('phone', { required: 'Phone number is required' })} />
        )}
        <div className="relative">
          <Input id="l-password" type={showPw ? 'text' : 'password'} label="Password"
            placeholder="••••••••" error={errors.password?.message}
            {...register('password', rules.password)} />
          <button type="button" onClick={() => setShowPw(s => !s)}
            className="absolute right-3 top-8 text-gray-400 hover:text-gray-600" aria-label="Toggle">
            <EyeIcon show={showPw} />
          </button>
        </div>
        {tab === 'email' && (
          <div className="flex justify-end">
            <button type="button" onClick={() => goTo('forgot-password')}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium">
              Forgot password?
            </button>
          </div>
        )}
        <Button type="submit" isLoading={isLoading} className="w-full" size="lg">Sign In</Button>
      </form>

      <p className="text-center text-sm text-gray-500 mt-4">
        Don't have an account?{' '}
        <button onClick={() => goTo('register')} className="text-primary-600 font-semibold hover:text-primary-700">
          Create one
        </button>
      </p>

      <div className="flex items-center gap-3 my-3">
        <hr className="flex-1 border-gray-200" />
        <span className="text-xs text-gray-400">or</span>
        <hr className="flex-1 border-gray-200" />
      </div>
      <SocialButtons onSuccess={handleSocialSuccess} />
    </>
  );
};

// ── Register ──────────────────────────────────────────────────────────────
const StepRegister = () => {
  const { setAuth } = useAuthStore();
  const { goTo, closeLoginModal } = useAuthModal();
  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const [showPw, setShowPw] = useState(false);
  const [tab, setTab] = useState('email');
  const password = watch('password', '');
  const checks = strengthChecks(password);
  const strength = checks.filter(c => c.pass).length;

  const { mutate, isLoading } = useMutation({
    mutationFn: registerApi,
    onSuccess: ({ data }) => {
      toast.success(data.message);
      goTo('verify-otp', { email: data.data.email, purpose: 'email-verify' });
    },
  });

  const onSubmit = (d) => {
    if (tab === 'phone') { toast.info('Phone signup coming soon!'); return; }
    mutate({ name: d.name, email: d.email, password: d.password });
  };

  const handleSocialSuccess = (user, token, message) => {
    setAuth(user, token);
    toast.success(message || 'Logged in with Google!');
    closeLoginModal();
  };

  return (
    <>
      <div className="mb-5">
        <h2 className="text-2xl font-bold text-gray-900">Create account</h2>
        <p className="text-gray-500 mt-1 text-sm">Get started in minutes</p>
      </div>

      <Tabs active={tab} onChange={setTab} />

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        <Input id="r-name" type="text" label="Full name" placeholder="John Doe"
          error={errors.name?.message} {...register('name', rules.required('Name'))} />

        {tab === 'email' ? (
          <Input id="r-email" type="email" label="Email address" placeholder="you@example.com"
            error={errors.email?.message} {...register('email', rules.email)} />
        ) : (
          <Input id="r-phone" type="tel" label="Phone Number" placeholder="03XX-XXXXXXX"
            error={errors.phone?.message}
            {...register('phone', { required: 'Phone number is required' })} />
        )}

        <div>
          <div className="relative">
            <Input id="r-password" type={showPw ? 'text' : 'password'} label="Password"
              placeholder="••••••••" error={errors.password?.message}
              {...register('password', {
                ...rules.password,
                validate: {
                  uppercase: v => /[A-Z]/.test(v) || 'Must contain an uppercase letter',
                  number:    v => /[0-9]/.test(v) || 'Must contain a number',
                },
              })} />
            <button type="button" onClick={() => setShowPw(s => !s)}
              className="absolute right-3 top-8 text-gray-400 hover:text-gray-600" aria-label="Toggle">
              <EyeIcon show={showPw} />
            </button>
          </div>
          {password && (
            <div className="mt-2 space-y-1.5">
              <div className="flex gap-1">
                {[1,2,3].map(i => (
                  <div key={i} className={cn('h-1.5 flex-1 rounded-full transition-all',
                    i <= strength ? strength===1?'bg-red-400':strength===2?'bg-yellow-400':'bg-green-500' : 'bg-gray-200')} />
                ))}
              </div>
              <ul className="space-y-0.5">
                {checks.map(c => (
                  <li key={c.label} className={cn('flex items-center gap-1.5 text-xs', c.pass?'text-green-600':'text-gray-400')}>
                    {c.pass
                      ? <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                      : <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd"/></svg>
                    }
                    {c.label}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <Button type="submit" isLoading={isLoading} className="w-full" size="lg">Create Account</Button>
      </form>

      <p className="text-center text-sm text-gray-500 mt-4">
        Already have an account?{' '}
        <button onClick={() => goTo('login')} className="text-primary-600 font-semibold hover:text-primary-700">
          Sign in
        </button>
      </p>

      <div className="flex items-center gap-3 my-3">
        <hr className="flex-1 border-gray-200" />
        <span className="text-xs text-gray-400">or</span>
        <hr className="flex-1 border-gray-200" />
      </div>
      <SocialButtons onSuccess={handleSocialSuccess} />
    </>
  );
};

// ── Verify OTP ────────────────────────────────────────────────────────────
const RESEND_COOLDOWN = 60;
const StepVerifyOTP = () => {
  const { goTo, closeLoginModal, modalEmail, otpPurpose } = useAuthModal();
  const { setAuth } = useAuthStore();
  const [otp, setOtp] = useState('');
  const [countdown, setCountdown] = useState(RESEND_COOLDOWN);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const { mutate: verify, isLoading: verifying } = useMutation({
    mutationFn: (data) => otpPurpose === 'forgot-password' ? verifyResetOTPApi(data) : verifyOTPApi(data),
    onSuccess: ({ data }) => {
      if (otpPurpose === 'forgot-password') {
        toast.success('OTP verified');
        goTo('reset-password', { resetToken: data.data.resetToken });
      } else {
        setAuth(data.data.user, data.data.token);
        toast.success(data.message);
        closeLoginModal();
      }
    },
  });

  const { mutate: resend, isLoading: resending } = useMutation({
    mutationFn: resendOTPApi,
    onSuccess: ({ data }) => { toast.success(data.message); setCountdown(RESEND_COOLDOWN); setOtp(''); },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (otp.length !== 6) return toast.error('Enter all 6 digits');
    verify({ email: modalEmail, otp });
  };

  return (
    <>
      <div className="text-center mb-6">
        <div className="w-14 h-14 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-7 h-7 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Check your email</h2>
        <p className="text-gray-500 mt-1 text-sm">We sent a 6-digit code to</p>
        <p className="font-semibold text-gray-800 text-sm">{modalEmail}</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-5">
        <OTPInput onChange={setOtp} disabled={verifying} />
        <Button type="submit" isLoading={verifying} className="w-full" size="lg">Verify Code</Button>
      </form>
      <div className="text-center mt-4">
        {countdown > 0
          ? <p className="text-sm text-gray-400">Resend in <span className="font-semibold text-gray-600 tabular-nums">{countdown}s</span></p>
          : <button onClick={() => resend({ email: modalEmail, purpose: otpPurpose })} disabled={resending}
              className="text-sm text-primary-600 font-semibold hover:text-primary-700 disabled:opacity-50">
              {resending ? 'Sending...' : 'Resend code'}
            </button>
        }
      </div>
      <div className="text-center mt-3">
        <button onClick={() => goTo('login')} className="text-sm text-gray-400 hover:text-gray-600">← Back to login</button>
      </div>
    </>
  );
};

// ── Forgot Password ───────────────────────────────────────────────────────
const StepForgotPassword = () => {
  const { goTo } = useAuthModal();
  const { register, handleSubmit, formState: { errors } } = useForm();

  const { mutate, isLoading } = useMutation({
    mutationFn: forgotPasswordApi,
    onSuccess: ({ data }) => {
      toast.success(data.message);
      goTo('verify-otp', { email: data.data.email, purpose: 'forgot-password' });
    },
  });

  return (
    <>
      <div className="text-center mb-6">
        <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-7 h-7 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Forgot password?</h2>
        <p className="text-gray-500 mt-1 text-sm">Enter your email and we'll send a reset code.</p>
      </div>
      <form onSubmit={handleSubmit((d) => mutate(d))} noValidate className="space-y-4">
        <Input id="fp-email" type="email" label="Email address" placeholder="you@example.com"
          error={errors.email?.message} {...register('email', rules.email)} />
        <Button type="submit" isLoading={isLoading} className="w-full" size="lg">Send Reset Code</Button>
      </form>
      <div className="text-center mt-4">
        <button onClick={() => goTo('login')} className="text-sm text-gray-400 hover:text-gray-600">← Back to login</button>
      </div>
    </>
  );
};

// ── Reset Password ────────────────────────────────────────────────────────
const StepResetPassword = () => {
  const { goTo, modalResetToken } = useAuthModal();
  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const [showPw, setShowPw] = useState(false);
  const [showCf, setShowCf] = useState(false);
  const password = watch('password', '');

  const { mutate, isLoading } = useMutation({
    mutationFn: (data) => resetPasswordApi({ resetToken: modalResetToken, password: data.password }),
    onSuccess: ({ data }) => { toast.success(data.message); goTo('login'); },
  });

  return (
    <>
      <div className="text-center mb-6">
        <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Set new password</h2>
        <p className="text-gray-500 mt-1 text-sm">Choose a strong password</p>
      </div>
      <form onSubmit={handleSubmit((d) => mutate(d))} noValidate className="space-y-4">
        <div className="relative">
          <Input id="rp-password" type={showPw ? 'text' : 'password'} label="New password"
            placeholder="••••••••" error={errors.password?.message}
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 8, message: 'At least 8 characters' },
              validate: {
                uppercase: v => /[A-Z]/.test(v) || 'Must contain an uppercase letter',
                number:    v => /[0-9]/.test(v) || 'Must contain a number',
              },
            })} />
          <button type="button" onClick={() => setShowPw(s => !s)}
            className="absolute right-3 top-8 text-gray-400 hover:text-gray-600" aria-label="Toggle">
            <EyeIcon show={showPw} />
          </button>
        </div>
        <div className="relative">
          <Input id="rp-confirm" type={showCf ? 'text' : 'password'} label="Confirm password"
            placeholder="••••••••" error={errors.confirmPassword?.message}
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: v => v === password || 'Passwords do not match',
            })} />
          <button type="button" onClick={() => setShowCf(s => !s)}
            className="absolute right-3 top-8 text-gray-400 hover:text-gray-600" aria-label="Toggle">
            <EyeIcon show={showCf} />
          </button>
        </div>
        <Button type="submit" isLoading={isLoading} className="w-full" size="lg">Reset Password</Button>
      </form>
    </>
  );
};

// ── Modal Shell ───────────────────────────────────────────────────────────
const STEPS = {
  'login':           StepLogin,
  'register':        StepRegister,
  'verify-otp':      StepVerifyOTP,
  'forgot-password': StepForgotPassword,
  'reset-password':  StepResetPassword,
};

const LoginModal = () => {
  const { isOpen, step, openLoginModal, closeLoginModal } = useAuthModal();

  useEffect(() => {
    registerLoginModalTrigger(openLoginModal);
    return () => registerLoginModalTrigger(null);
  }, [openLoginModal]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') closeLoginModal(); };
    if (isOpen) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, closeLoginModal]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  const StepComponent = STEPS[step] || StepLogin;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closeLoginModal} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-8 animate-slide-up max-h-[90vh] overflow-y-auto">
        <button onClick={closeLoginModal}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <StepComponent />
      </div>
    </div>
  );
};

export default LoginModal;
